/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlinx.serialization.Serializable

/**
 * Key Identity. Can be used to find matching keys for decryption or verifying, etc. The identity
 * may contain [KDF] parameters if the corresponding key could be derived from a password.
 * Note that [kdp] part is not respected in [equals].
 *
 * Important. `KeyId` of matching keys are the same, so you can use it to identify
 * and find matching keys in the [UniversalRing], etc. For example:
 *
 * - [DecryptingSecretKey] and [EncryptingPublicKey] from the same pair have the same `KeyId`, thus the former
 *   can decrypt what was encrypted with the latter.
 *
 * - [SigningSecretKey] and corresponding [VerifyingKey] have the same `KeyId`. Use it to pick a proper key for
 *   signing from a ring with [UniversalRing.findKey]
 *
 * See [PBKD.Params.deriveKey] for deriving keys from id.
 *
 * See [id], and [BinaryId] class for more. Note that for [EncryptingPublicKey] and [VerifyingPublicKey] [BinaryId.asPublicKey]
 * and [BinaryId.asVerifyingKey] restore actual keys, providing [BinaryId.magic] has proper value, see [KeysmagicNumber]]
 *
 * @param id actual id used in equality test amd hash code generation. `Id` of the matching keys is the same.
 * @param kdp optional key derivation parameters. Does not affect equality. Allow deriving the key from proper
 *            password, see above.
 */
@Serializable
data class KeyId(val id: BinaryId, val kdp: PBKD.Params? = null) {

    /**
     * Binary array representation of the [id], not including the [kdp]. Used in [SafeKeyExchange]
     * and other key exchanges to generate session tokens, etc.
     *
     * In shortcut for packed [BinaryId], from [id]. If you need only key bytes, use [UniversalKey.keyBytes].
     */
    val binaryTag: UByteArray by lazy { id.id }

    val keymagic: KeysmagicNumber by lazy { KeysmagicNumber.entries[id.magic] }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is KeyId) return false
        return id == other.id
    }

    override fun hashCode(): Int {
        return id.hashCode()
    }

    override fun toString() = id.toString()

    constructor(
        magicNumber: KeysmagicNumber,
        keyData: UByteArray,
        kdp: PBKD.Params? = null,
        donNotHash: Boolean = false,
    )
            : this(
        BinaryId.createFromUBytes(
            magicNumber.ordinal,
            if (donNotHash) keyData else blake2b3l(keyData)
        ),
        kdp
    )
}